import { Box, Typography, styled, Button } from "@mui/material";
import { colors } from "../../../theme/colors";

export const ContentContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "60px",
  width: "100%",
});

export const ModalTitle = styled(Typography)({
  fontSize: "26px",
  fontWeight: 700,
  color: colors.text.primary,
  textAlign: "center",
  fontFamily: "Sora, sans-serif",
  marginBottom: "16px",
});

export const ModalDescription = styled(Typography)({
  fontSize: "16px",
  fontWeight: 500,
  color: colors.primary.main,
  textAlign: "center",
  fontFamily: "Nunito, sans-serif",
  marginBottom: "44px",
});

export const DropZone = styled(Box)<{ isDragging?: boolean }>(
  ({ isDragging }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    width: "100%",
    border: `2px dashed ${isDragging ? colors.primary.main : colors.border.medium}`,
    borderRadius: "8px",
    backgroundColor: isDragging ? colors.background.lightBlue : "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",

    "&:hover": {
      borderColor: colors.primary.main,
      backgroundColor: colors.background.lightBlue,
    },
  })
);

export const DropZoneText = styled(Typography)({
  fontSize: "14px",
  fontWeight: 500,
  color: colors.text.primary,
  textAlign: "center",
  fontFamily: "Nunito, sans-serif",
  marginTop: "8px",
});

export const DropZoneLink = styled("span")({
  color: colors.primary.main,
  cursor: "pointer",
  textDecoration: "underline",
});

export const FileListContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
  marginTop: "24px",
});

export const FileItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 16px",
  backgroundColor: colors.background.secondary,
  borderRadius: "8px",
  width: "100%",
});

export const FileInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
});

export const FileName = styled(Typography)({
  fontSize: "14px",
  fontWeight: 500,
  color: colors.text.primary,
  fontFamily: "Nunito, sans-serif",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const ProgressBar = styled(Box)<{ progress: number; error?: boolean }>(
  ({ progress, error }) => ({
    width: "100%",
    height: "4px",
    backgroundColor: colors.border.light,
    borderRadius: "2px",
    marginTop: "4px",
    overflow: "hidden",

    "&::after": {
      content: '""',
      display: "block",
      width: `${progress}%`,
      height: "100%",
      backgroundColor: error ? colors.status.error : colors.amber.main,
      borderRadius: "2px",
      transition: "width 0.3s ease",
    },
  })
);

export const RemoveButton = styled(Button)({
  minWidth: "auto",
  padding: "4px",
  color: colors.text.secondary,

  "&:hover": {
    backgroundColor: colors.background.lightBlue,
  },
});

export const ButtonsContainer = styled(Box)({
  display: "flex",
  gap: "24px",
  justifyContent: "center",
  marginTop: "32px",
});

export const CancelButton = styled(Button)({
  height: "48px",
  minWidth: "140px",
  borderRadius: "8px",
  border: `1px solid ${colors.border.medium}`,
  color: colors.primary.main,
  fontWeight: 600,
  fontSize: "16px",
  textTransform: "none",
  fontFamily: "Nunito, sans-serif",
  backgroundColor: "transparent",

  "&:hover": {
    backgroundColor: colors.background.lightBlue,
    borderColor: colors.border.medium,
  },
});

export const ImportButton = styled(Button)<{ disabled?: boolean }>(
  ({ disabled }) => ({
    height: "48px",
    minWidth: "140px",
    borderRadius: "8px",
    backgroundColor: disabled ? colors.border.light : colors.amber.main,
    color: disabled ? colors.text.secondary : "#FFFFFF",
    fontWeight: 600,
    fontSize: "16px",
    textTransform: "none",
    fontFamily: "Nunito, sans-serif",
    boxShadow: "none",

    "&:hover": {
      backgroundColor: disabled ? colors.border.light : colors.amber.dark,
      boxShadow: "none",
    },
  })
);

// Success/Failed states
export const ResultImage = styled("img")({
  width: "120px",
  height: "auto",
  marginBottom: "24px",
});

export const ResultTitle = styled(Typography)({
  fontSize: "26px",
  fontWeight: 700,
  color: colors.text.primary,
  textAlign: "center",
  fontFamily: "Sora, sans-serif",
  marginBottom: "16px",
});

export const ResultDescription = styled(Typography)({
  fontSize: "16px",
  fontWeight: 500,
  color: colors.text.secondary,
  textAlign: "center",
  fontFamily: "Nunito, sans-serif",
  marginBottom: "32px",
  maxWidth: "400px",
});

export const DoneButton = styled(Button)({
  height: "48px",
  minWidth: "140px",
  borderRadius: "8px",
  backgroundColor: colors.amber.main,
  color: "#FFFFFF",
  fontWeight: 600,
  fontSize: "16px",
  textTransform: "none",
  fontFamily: "Nunito, sans-serif",
  boxShadow: "none",

  "&:hover": {
    backgroundColor: colors.amber.dark,
    boxShadow: "none",
  },
});

export const ErrorList = styled("ol")({
  textAlign: "left",
  paddingLeft: "20px",
  marginBottom: "32px",
  maxWidth: "400px",
});

export const ErrorItem = styled("li")({
  fontSize: "14px",
  fontWeight: 400,
  color: colors.text.secondary,
  fontFamily: "Nunito, sans-serif",
  marginBottom: "8px",
  lineHeight: "1.5",
});

export const HiddenInput = styled("input")({
  display: "none",
});

